import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import SammiLogo from '../components/common/SammiLogo';
import PaymentForm from '../components/common/PaymentForm';
import { useSubscription } from '../components/common/subscription-context/SubscriptionContext';
import OrganizationService from '../services/organization.service';
import { ExclamationTriangleIcon, CreditCardIcon } from '@heroicons/react/24/outline';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_12345');

const PastDuePaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { organizationName, clearPastDue } = useSubscription();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [cardsOnFile, setCardsOnFile] = useState<Array<{
    id: string;
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
    isDefault: boolean;
  }>>([]);
  const [selectedExistingCard, setSelectedExistingCard] = useState<string | null>(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);

  // Get customer email from JWT token
  const token = localStorage.getItem('accessToken');
  const decodedToken = token ? JSON.parse(atob(token.split('.')[1])) : null;
  const customerEmail = decodedToken?.email || '';

  // Fall back to localStorage if context was lost (e.g. direct navigation)
  const displayOrgName = organizationName || localStorage.getItem('organizationName') || '';

  // On mount, ask the BE to create a SetupIntent for the existing Stripe customer.
  // The BE only needs to: look up stripeCustomerId, call stripe.setupIntents.create(), return clientSecret.
  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        // Single call returns both the SetupIntent client secret and existing cards on file
        const { clientSecret, paymentMethods } = await OrganizationService.createSetupIntent();
        if (!cancelled) {
          setClientSecret(clientSecret);
          if (paymentMethods && paymentMethods.length > 0) {
            setCardsOnFile(paymentMethods);
          } else {
            // No existing cards — go straight to new card form
            setShowNewCardForm(true);
          }
        }
      } catch (error) {
        if (!cancelled) {
          const msg = error instanceof Error ? error.message : 'Failed to initialize payment form.';
          setErrorMessage(msg);
        }
      } finally {
        if (!cancelled) {
          setPageLoading(false);
        }
      }
    };

    initialize();
    return () => { cancelled = true; };
  }, []);

  // Handle retrying with an existing card on file
  const handleUseExistingCard = async () => {
    if (!selectedExistingCard) return;
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const result = await OrganizationService.resolvePastDueSubscription(selectedExistingCard);

      if (result.subscriptionStatus === 'past_due') {
        setErrorMessage(
          'The overdue payment could not be processed with this card. ' +
          'Please verify your card has sufficient funds or try a different card.'
        );
        setIsSubmitting(false);
        return;
      }

      setSuccessMessage('Payment successful! Redirecting...');
      clearPastDue();
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Payment processing failed. Please try again.';
      setErrorMessage(message);
      setIsSubmitting(false);
    }
  };

  // Called after stripe.confirmCardSetup() succeeds.
  // At this point the PaymentMethod is already attached to the Stripe Customer
  // (Stripe did that automatically). We just need the BE to:
  //   1. Set it as the default PM on the subscription
  //   2. Retry the past-due invoice
  const handlePaymentSuccess = async (paymentMethodId: string) => {
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Tell the BE to set this PM as default and retry the invoice
      const result = await OrganizationService.resolvePastDueSubscription(paymentMethodId);

      // Check the result — did the invoice retry succeed?
      if (result.subscriptionStatus === 'past_due') {
        setErrorMessage(
          'Your new card was saved, but the overdue payment could not be processed. ' +
          'Please verify your card has sufficient funds or try a different card.'
        );
        // Get a fresh SetupIntent so they can try again with a new card
        try {
          const fresh = await OrganizationService.createSetupIntent();
          setClientSecret(fresh.clientSecret);
        } catch { /* ignore — they'll see the error */ }
        setIsSubmitting(false);
        return;
      }

      // Payment confirmed — clear past_due state and let the user in
      setSuccessMessage('Payment successful! Redirecting...');
      clearPastDue();

      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Payment processing failed. Please try again.';
      setErrorMessage(message);
      setIsSubmitting(false);
    }
  };

  const handlePaymentError = (error: string) => {
    setErrorMessage(error);
    setIsSubmitting(false);
  };

  return (
    <div 
      className="flex flex-col items-center justify-center min-h-screen px-4"
      style={{
        background: 'linear-gradient(135deg, rgba(25, 183, 223, 0.75) 0%, rgba(65, 239, 170, 0.75) 100%), linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}
    >
      <div className="flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 w-full max-w-md mx-auto">
        <SammiLogo size="large" className="mb-6" />

        {/* Warning Alert */}
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-8 w-full">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900 text-sm mb-1">Payment Overdue</h3>
            <p className="text-sm text-red-800">
              {displayOrgName && <span className="font-semibold">{displayOrgName}'s</span>}
              {' '}subscription payment is overdue. Please update your payment information to continue using Fieldgistix Manager.
            </p>
          </div>
        </div>

        {/* Payment Section */}
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-2" style={{ color: '#002457' }}>
          Update Payment Information
        </h2>
        
        <p className="text-center text-gray-600 text-sm mb-8">
          Securely update your payment method to restore full access to your account.
        </p>

        {errorMessage && (
          <div className="p-4 rounded-2xl bg-red-500 text-white mb-6 text-center text-sm w-full">
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div className="p-4 rounded-2xl bg-green-500 text-white mb-6 text-center text-sm w-full">
            {successMessage}
          </div>
        )}

        {/* Payment Section */}
        {pageLoading ? (
          <div className="flex items-center justify-center py-8 w-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800" />
            <span className="ml-3 text-gray-600 text-sm">Preparing payment form...</span>
          </div>
        ) : (
          <div className="w-full">
            {/* Cards on File */}
            {cardsOnFile.length > 0 && (
              <div className="mb-6 w-full">
                <h3 className="text-sm font-semibold mb-3" style={{ color: '#002457' }}>Cards on File</h3>
                <div className="space-y-2">
                  {cardsOnFile.map((card) => (
                    <button
                      key={card.id}
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        setSelectedExistingCard(card.id);
                        setShowNewCardForm(false);
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-colors duration-200 text-left ${
                        selectedExistingCard === card.id && !showNewCardForm
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <CreditCardIcon className="h-6 w-6 text-gray-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {card.brand} •••• {card.last4}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          Exp {String(card.expMonth).padStart(2, '0')}/{card.expYear}
                        </span>
                        {card.isDefault && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Default
                          </span>
                        )}
                      </div>
                      <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                        selectedExistingCard === card.id && !showNewCardForm
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedExistingCard === card.id && !showNewCardForm && (
                          <div className="h-full w-full flex items-center justify-center">
                            <div className="h-1.5 w-1.5 rounded-full bg-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Retry with existing card button */}
                {selectedExistingCard && !showNewCardForm && (
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleUseExistingCard}
                    className="w-full mt-4 py-3 px-4 rounded-2xl text-white font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: isSubmitting ? '#94a3b8' : 'linear-gradient(135deg, #19B7DF 0%, #41EFAA 100%)' }}
                  >
                    {isSubmitting ? 'Processing Payment...' : 'Retry Payment with This Card'}
                  </button>
                )}

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-3 text-gray-500">or</span>
                  </div>
                </div>

                {/* Toggle for new card form */}
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    setShowNewCardForm(!showNewCardForm);
                    setSelectedExistingCard(null);
                  }}
                  className={`w-full py-3 px-4 rounded-2xl font-semibold text-sm transition-all duration-200 border-2 ${
                    showNewCardForm
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {showNewCardForm ? 'Using New Card' : 'Use a New Card Instead'}
                </button>
              </div>
            )}

            {/* New Card Form */}
            {(showNewCardForm || cardsOnFile.length === 0) && clientSecret ? (
              <Elements stripe={stripePromise}>
                <PaymentForm 
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  isSubmitting={isSubmitting}
                  setIsSubmitting={setIsSubmitting}
                  organizationName={displayOrgName || 'Your Organization'}
                  customerEmail={customerEmail}
                  submitLabel="Update Payment & Resolve Balance"
                  submittingLabel="Processing Payment..."
                  clientSecret={clientSecret}
                />
              </Elements>
            ) : (showNewCardForm || cardsOnFile.length === 0) && !clientSecret ? (
              <div className="text-center text-gray-600 text-sm py-8 w-full">
                <p>Unable to load the payment form. Please refresh the page or contact support.</p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default PastDuePaymentPage;
